import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { storage } from '../../utils/storage';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  ArrowLeftIcon, 
  PencilIcon, 
  DocumentIcon, 
  FolderOpenIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  PhotoIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [event, setEvent] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  
  // Preview modal state
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Audio/Video refs
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  // Get the base URL for API calls
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:9090';
    }
    return process.env.REACT_APP_API_URL || 'http://localhost:9090';
  };

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      console.log('📡 Fetching event:', id);
      const { data } = await get(`/api/events/${id}`);
      if (data) {
        console.log('✅ Event fetched:', data);
        setEvent(data);
        fetchFilesByEvent(id);
      }
    } catch (error) {
      console.error('❌ Error fetching event:', error);
    }
  };

  const fetchFilesByEvent = async (eventId) => {
    setLoadingFiles(true);
    try {
      console.log('📡 Fetching files for event:', eventId);
      const { data } = await get(`/api/files/event/${eventId}`);
      if (data) {
        console.log('✅ Files fetched:', data.length, 'files');
        setFiles(data);
      }
    } catch (error) {
      console.error('❌ Error fetching files:', error);
      if (event?.fileIds && event.fileIds.length > 0) {
        fetchFileDetails(event.fileIds);
      }
    } finally {
      setLoadingFiles(false);
    }
  };

  const fetchFileDetails = async (fileIds) => {
    if (!fileIds || fileIds.length === 0) return;
    
    setLoadingFiles(true);
    try {
      const filePromises = fileIds.map(fileId => get(`/api/files/${fileId}`));
      const responses = await Promise.all(filePromises);
      const fileData = responses.map(res => res.data).filter(Boolean);
      console.log('✅ File details fetched:', fileData.length, 'files');
      setFiles(fileData);
    } catch (error) {
      console.error('❌ Error fetching file details:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // File type detection
  const getFileExtension = (filePath) => {
    if (!filePath) return '';
    const parts = filePath.split('.');
    return parts[parts.length - 1].toLowerCase();
  };

  const getFileTypeCategory = (file) => {
    const path = file.path || file.filePath || '';
    const ext = getFileExtension(path);
    
    if (['mp4', 'mov', 'avi', 'webm', 'mkv', 'flv', 'wmv'].includes(ext)) {
      return 'video';
    }
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) {
      return 'audio';
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(ext)) {
      return 'image';
    }
    if (['pdf'].includes(ext)) {
      return 'pdf';
    }
    if (['txt', 'js', 'html', 'css', 'json', 'xml'].includes(ext)) {
      return 'text';
    }
    
    if (file.type?.fileName) {
      const typeName = file.type.fileName.toLowerCase();
      if (typeName.includes('video')) return 'video';
      if (typeName.includes('voice') || typeName.includes('audio')) return 'audio';
      if (typeName.includes('thumbnail') || typeName.includes('image')) return 'image';
    }
    
    return 'other';
  };

  const getFileIcon = (file) => {
    const category = getFileTypeCategory(file);
    switch (category) {
      case 'video':
        return <VideoCameraIcon className="h-5 w-5 text-purple-500" />;
      case 'audio':
        return <MusicalNoteIcon className="h-5 w-5 text-green-500" />;
      case 'image':
        return <PhotoIcon className="h-5 w-5 text-blue-500" />;
      case 'pdf':
        return <DocumentTextIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getFileName = (file) => {
    if (file.fileName) return file.fileName;
    if (file.path) return file.path.split('/').pop();
    if (file.filePath) return file.filePath.split('/').pop();
    return `File #${file.id}`;
  };

  const getFileSize = (file) => {
    return file.fileSize || file.size || 0;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getStreamUrl = (fileId) => {
    const baseUrl = getApiBaseUrl();
    // return `${baseUrl}/api/files/stream/${fileId}`;
    const token = storage.getToken();
    return `${baseUrl}/api/files/stream/${fileId}?token=${encodeURIComponent(token)}`;
  };

  const getDownloadUrl = (fileId) => {
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/api/files/download/${fileId}`;
  };

  // Handle file preview
  const handlePreview = async (file) => {
    setSelectedFile(file);
    setPreviewError(null);
    setShowPreviewModal(true);

    const category = getFileTypeCategory(file);

    // Video and audio: let the browser stream directly via src,
    // so it can issue its own Range requests and start playback immediately.
    if (category === 'video' || category === 'audio') {
      const token = storage.getToken();

      if (!token) {
        setPreviewError('No authentication token found');
        return;
      }

      if (storage.isTokenExpired()) {
        storage.clear();
        navigate('/login?session=expired');
        setPreviewError('Session expired. Please login again.');
        return;
      }

      // No fetch/blob here — just set the streaming URL and let <video>/<audio> load it.
      setSelectedFile({
        ...file,
        streamUrl: getStreamUrl(file.id),
      });
      setPreviewLoading(false);
      return;
    }

    // Images, PDFs, text: fully load via blob (no seeking/progressive needs here,
    // and it lets us send the Authorization header properly).
    setPreviewLoading(true);

    const baseUrl = getApiBaseUrl();
    const streamUrl = `${baseUrl}/api/files/stream/${file.id}`;

    try {
      const token = storage.getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (storage.isTokenExpired()) {
        storage.clear();
        navigate('/login?session=expired');
        throw new Error('Session expired. Please login again.');
      }

      const response = await fetch(streamUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: '*/*',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        let errorMessage = `Failed to load file: ${response.status}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            const text = await response.text();
            if (text) errorMessage = text.substring(0, 100);
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        if (response.status === 401) {
          storage.clear();
          navigate('/login?session=expired');
          throw new Error('Session expired. Please login again.');
        }
        if (response.status === 403) {
          throw new Error('You do not have permission to access this file.');
        }
        if (response.status === 404) {
          throw new Error('File not found on server. Please check if the file exists.');
        }

        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('File is empty (0 bytes)');
      }

      const objectUrl = URL.createObjectURL(blob);

      setSelectedFile({
        ...file,
        objectUrl,
      });
      setPreviewLoading(false);
    } catch (error) {
      console.error('❌ Error loading file preview:', error);
      setPreviewError(error.message);
      setPreviewLoading(false);
    }
  };

    // Handle file download
    const handleDownload = async (file) => {
      console.log('⬇️ Downloading file:', file);
      setDownloadLoading(true);
      try {
        const downloadUrl = getDownloadUrl(file.id);
        console.log('📡 Download URL:', downloadUrl);
        
        const token = storage.getToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': '*/*',
          },
        });
        
        console.log('📊 Response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 401) {
            storage.clear();
            navigate('/login?session=expired');
            throw new Error('Session expired. Please login again.');
          }
          throw new Error(`Download failed: ${response.status}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getFileName(file);
        document.body.appendChild(link);
        link.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        console.log('✅ Download successful');
      } catch (error) {
        console.error('❌ Download error:', error);
        alert(`Failed to download file: ${error.message}`);
      } finally {
        setDownloadLoading(false);
      }
  };

  // Close preview modal
  const closePreview = () => {
    setShowPreviewModal(false);
    setSelectedFile(null);
    setPreviewError(null);
    setPreviewLoading(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Render preview content
  const renderPreviewContent = () => {
    if (!selectedFile) return null;

    if (previewLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-white">Loading file...</p>
        </div>
      );
    }

    if (previewError) {
      return (
        <div className="text-center py-12">
          <p className="text-red-500 text-lg mb-4">Error loading file</p>
          <p className="text-gray-400 text-sm mb-4">{previewError}</p>
          <button
            onClick={() => handleDownload(selectedFile)}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
          >
            Download Instead
          </button>
        </div>
      );
    }

    const category = getFileTypeCategory(selectedFile);
    const fileName = getFileName(selectedFile);
    const fileSize = formatFileSize(getFileSize(selectedFile));
    const fileType = selectedFile.type?.fileName || 'Unknown';

    // For images
    if (category === 'image' && selectedFile.objectUrl) {
      return (
        <div className="flex flex-col items-center w-full">
          <img
            src={selectedFile.objectUrl}
            alt={fileName}
            className="max-h-[70vh] max-w-full object-contain rounded-lg"
            onError={(e) => {
              console.error('❌ Image load error');
              setPreviewError('Failed to load image');
              e.target.src = '/images/file-error.png';
            }}
          />
          <div className="mt-4 text-center text-white">
            <p className="text-sm font-medium">{fileName}</p>
            <p className="text-xs text-gray-400">{fileSize} • {fileType}</p>
          </div>
        </div>
      );
    }

    // For PDF
    if (category === 'pdf' && selectedFile.objectUrl) {
      return (
        <div className="flex flex-col items-center w-full h-[70vh]">
          <iframe
            src={`${selectedFile.objectUrl}#toolbar=1`}
            className="w-full h-full rounded-lg"
            title={fileName}
          />
        </div>
      );
    }

    // For text
    if (category === 'text' && selectedFile.objectUrl) {
      return (
        <div className="flex flex-col items-center w-full">
          <iframe
            src={selectedFile.objectUrl}
            className="w-full h-[60vh] rounded-lg bg-white"
            title={fileName}
          />
          <div className="mt-4 text-center text-white">
            <p className="text-sm font-medium">{fileName}</p>
            <p className="text-xs text-gray-400">{fileSize} • {fileType}</p>
          </div>
        </div>
      );
    }

    // For video
    if (category === 'video') {
      return (
        <div className="flex flex-col items-center w-full">
          <video
            ref={videoRef}
            controls
            className="max-h-[70vh] max-w-full rounded-lg"
            autoPlay
            controlsList="nodownload"
            onError={(e) => {
              const mediaError = e.target.parentElement?.error;
              console.error('❌ Video load error:', mediaError?.code, mediaError?.message);
              console.error('❌ Failed src:', selectedFile.streamUrl);
              setPreviewError('Failed to load video');
            }}
          >
            <source src={selectedFile.streamUrl} />
            Your browser does not support the video tag.
          </video>
          <div className="mt-4 text-center text-white">
            <p className="text-sm font-medium">{fileName}</p>
            <p className="text-xs text-gray-400">{fileSize} • {fileType}</p>
          </div>
        </div>
      );
    }

    // For audio
    if (category === 'audio') {
      return (
        <div className="flex flex-col items-center w-full max-w-md">
          <div className="w-32 h-32 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-6">
            <MusicalNoteIcon className="h-16 w-16 text-white" />
          </div>
          <p className="text-lg font-medium text-white mb-2">{fileName}</p>
          <p className="text-sm text-gray-400 mb-6">{fileSize} • {fileType}</p>
          <audio
            ref={audioRef}
            controls
            className="w-full"
            autoPlay
            controlsList="nodownload"
            onError={(e) => {
              const mediaError = e.target.parentElement?.error;
              console.error('❌ Audio load error:', mediaError?.code, mediaError?.message);
              console.error('❌ Failed src:', selectedFile.streamUrl);
              setPreviewError('Failed to load audio');
            }}
          >
            <source src={selectedFile.streamUrl} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    // Fallback for other file types
    return (
      <div className="text-center py-12">
        <DocumentIcon className="h-24 w-24 text-gray-500 mx-auto mb-4" />
        <p className="text-white text-lg mb-2">Preview not available</p>
        <p className="text-gray-400 text-sm mb-4">{fileName}</p>
        <p className="text-gray-500 text-xs mb-6">{fileSize} • {fileType}</p>
        <button
          onClick={() => handleDownload(selectedFile)}
          disabled={downloadLoading}
          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 mx-auto disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          <span>Download File</span>
        </button>
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;
  if (!event) return <div className="p-6 text-gray-900 dark:text-white">Event not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/events/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Event Details</h1>
        </div>
        <button
          onClick={() => navigate(`/events/edit/${id}`)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
        >
          <PencilIcon className="h-5 w-5" />
          <span>Edit</span>
        </button>
      </div>

      {/* Event Information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">ID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{event.id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">UUID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white font-mono text-sm">
              {event.evtUuid || event.uuid || '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Trip ID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{event.tripId || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Event Type</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {event.eventType?.description || '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Reference Number</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{event.referenceNum || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Timestamp</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
            </p>
          </div>
          {event.voiceText && (
            <div className="md:col-span-2">
              <label className="text-sm text-gray-500 dark:text-gray-400">Voice Text</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white whitespace-pre-wrap bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                {event.voiceText}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Files Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <FolderOpenIcon className="h-5 w-5 text-indigo-600" />
            <span>Associated Files ({files.length})</span>
          </h2>
        </div>

        {loadingFiles ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : files.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {files.map((file) => {
              const category = getFileTypeCategory(file);
              const fileName = getFileName(file);
              const fileSize = formatFileSize(getFileSize(file));
              const fileType = file.type?.fileName || 'Unknown';
              
              return (
                <div 
                  key={file.id} 
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
                        {getFileIcon(file)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {fileName}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{fileSize}</span>
                          <span>•</span>
                          <span>{fileType}</span>
                          {category === 'video' && <span className="text-purple-600">🎬</span>}
                          {category === 'audio' && <span className="text-green-600">🎵</span>}
                          {category === 'image' && <span className="text-blue-600">🖼️</span>}
                          {file.isEncrypted && <span className="text-yellow-600">🔒</span>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handlePreview(file)}
                      className="flex-1 bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 text-sm flex items-center justify-center space-x-1 transition-colors"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>{category === 'audio' ? 'Play' : category === 'video' ? 'Play' : 'View'}</span>
                    </button>
                    
                    <button
                      onClick={() => handleDownload(file)}
                      disabled={downloadLoading}
                      className="flex-1 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 text-sm flex items-center justify-center space-x-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>{downloadLoading ? 'Downloading...' : 'Download'}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p>No files associated with this event</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedFile && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl max-h-full">
            {/* Close button */}
            <button
              onClick={closePreview}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 z-10 transition-colors"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>

            {/* Preview content */}
            <div className="bg-gray-900 rounded-lg flex items-center justify-center p-6 min-h-[200px]">
              {renderPreviewContent()}
            </div>

            {/* Actions */}
            <div className="flex justify-center space-x-4 mt-4">
              <button
                onClick={() => handleDownload(selectedFile)}
                disabled={downloadLoading}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span>{downloadLoading ? 'Downloading...' : 'Download'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;